import React from 'react';

import {
  JUDGE_SEARCH_SORT_OPTIONS,
  JUDGE_SELECTION_METHOD_OPTIONS,
  JUDGE_POLITICAL_AFFILIATION_OPTIONS,
  SEARCH_OPERATORS,
} from '../../constants';
import {
  SearchForm,
  type SearchFormData,
  type SearchTypeOption,
  type FilterChipConfig,
  type MoreSectionConfig,
} from '../shared';

import type { JudgeSearchType, JudgeSearchFormData } from '../types';

// Re-export types for convenience
export type { JudgeSearchType, JudgeSearchFormData };

export interface JudgeSearchFormProps {
  onSubmit?: (data: JudgeSearchFormData) => void;
  initialSearchType?: JudgeSearchType;
  initialQuery?: string;
  initialExactMatch?: boolean;
  sortValue?: string;
  isLoading?: boolean;
  disabled?: boolean;
}

const JUDGE_SEARCH_TYPE_OPTIONS: SearchTypeOption[] = [
  { value: 'keyword', label: 'Keyword', placeholder: 'Search by Keyword' },
  {
    value: 'name',
    label: 'Name',
    placeholder: 'Enter judge name (e.g., name: John Smith)',
    prefill: 'name:',
  },
  {
    value: 'court',
    label: 'Court',
    placeholder: 'Enter court name (e.g., court: Supreme Court)',
    prefill: 'court:',
  },
  {
    value: 'appointedBy',
    label: 'Appointed By',
    placeholder: 'Enter appointer name (e.g., appointedBy: President)',
    prefill: 'appointedBy:',
  },
];

const JUDGE_SEARCH_FILTER_CHIP_CONFIGS: FilterChipConfig[] = [
  {
    id: 'jurisdiction',
    label: 'Jurisdiction',
    type: 'jurisdiction',
  },
  {
    id: 'name',
    label: 'Name',
    type: 'text',
    fieldLabel: 'Judge Name',
    placeholder: 'e.g., John Smith',
  },
  {
    id: 'dobCity',
    label: 'Birth City',
    type: 'text',
    fieldLabel: 'Birth City',
    placeholder: 'e.g., New York',
  },
  {
    id: 'dobState',
    label: 'Birth State',
    type: 'text',
    fieldLabel: 'Birth State',
    placeholder: 'e.g., NY',
  },
  {
    id: 'school',
    label: 'School',
    type: 'text',
    fieldLabel: 'School',
    placeholder: 'e.g., Harvard Law School',
  },
];

const JUDGE_SEARCH_MORE_SECTION_CONFIG: MoreSectionConfig = {
  id: 'more',
  label: 'More',
  popoverTitle: 'Add More Details',
  fields: [
    {
      id: 'orderBy',
      label: 'Search Results Order',
      type: 'select',
      options: JUDGE_SEARCH_SORT_OPTIONS.map((option) => ({
        value: option.value,
        label: option.display,
      })),
      defaultValue: 'score_desc',
    },
    {
      id: 'selectionMethod',
      label: 'Selection Method',
      type: 'select',
      options: JUDGE_SELECTION_METHOD_OPTIONS.map((option) => ({
        value: option.value,
        label: option.display,
      })),
      defaultValue: '',
    },
    {
      id: 'politicalAffiliation',
      label: 'Political Affiliation',
      type: 'select',
      options: JUDGE_POLITICAL_AFFILIATION_OPTIONS.map((option) => ({
        value: option.value,
        label: option.display,
      })),
      defaultValue: '',
    },
    {
      id: 'bornAfter',
      label: 'Born After',
      type: 'text',
      placeholder: 'YYYY-MM-DD',
    },
    {
      id: 'bornBefore',
      label: 'Born Before',
      type: 'text',
      placeholder: 'YYYY-MM-DD',
    },
    {
      id: 'appointedBy',
      label: 'Appointed By',
      type: 'text',
      placeholder: 'e.g., President, Governor',
    },
  ],
};

export const JudgeSearchForm: React.FC<JudgeSearchFormProps> = ({
  onSubmit,
  initialSearchType = 'keyword',
  initialQuery = '',
  initialExactMatch = true,
  sortValue,
  isLoading = false,
  disabled = false,
}) => {
  const handleSubmit = (data: SearchFormData) => {
    if (onSubmit) {
      onSubmit({
        ...data,
        searchType: data.searchType as JudgeSearchType,
      });
    }
  };

  const initialFilters = sortValue
    ? { moreFilters: { orderBy: sortValue } }
    : undefined;

  return (
    <SearchForm
      title="Search Judges Across Federal and State Courts"
      proTip="Use the search type chips below to refine your search by keyword, name, court, or appointer."
      searchTypeOptions={JUDGE_SEARCH_TYPE_OPTIONS}
      filterChipConfigs={JUDGE_SEARCH_FILTER_CHIP_CONFIGS}
      moreSectionConfig={JUDGE_SEARCH_MORE_SECTION_CONFIG}
      onSubmit={handleSubmit}
      initialSearchType={initialSearchType}
      initialQuery={initialQuery}
      initialExactMatch={initialExactMatch}
      initialFilters={initialFilters}
      isLoading={isLoading}
      disabled={disabled}
      showExactMatch={true}
      operatorsData={SEARCH_OPERATORS}
      testId="judge-search-form"
    />
  );
};

export default JudgeSearchForm;
